import { useCallback } from 'react';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import useSelectResource from 'hooks/useSelectResource';
import { getText } from 'i18n/intl';
import useFetchMissingItems from 'pages/objectManager/hooks/useFetchMissingItems';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  changeUser,
  deleteUser,
  fetchUsersByIds,
  firstSelectedUserSelector,
  isFirstUserSaveDisabledSelector,
  isSecondUserSaveDisabledSelector,
  saveUser,
  secondSelectedUserSelector,
  setFirstSelectedUser,
  setSecondSelectedUser,
  usersMapSelector,
} from 'slices/user.slice';
import type { ITEUserWithTEIdPAuthSettings, TTEUser } from '@timeedit/types';
import { TEDrawer } from '@timeedit/ui-components';
import UserDetail from './UserDetail';

type UserDrawerProps = {
  confirmDiscardUserChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
};

export const UserDrawer = ({ confirmDiscardUserChanges }: UserDrawerProps) => {
  const dispatch = useAppDispatch();
  const userMap = useAppSelector(usersMapSelector);
  const firstUser = useAppSelector(firstSelectedUserSelector);
  const secondUser = useAppSelector(secondSelectedUserSelector);
  const firstUserSaveDisabled = useAppSelector(isFirstUserSaveDisabledSelector);
  const secondUserSaveDisabled = useAppSelector(
    isSecondUserSaveDisabledSelector
  );

  const setFirstResource = useCallback(
    (user: ITEUserWithTEIdPAuthSettings) =>
      dispatch(setFirstSelectedUser(user)),
    [dispatch]
  );

  const setSecondResource = useCallback(
    (user: ITEUserWithTEIdPAuthSettings) =>
      dispatch(setSecondSelectedUser(user)),
    [dispatch]
  );

  const selectedUserIds = useSelectResource(
    'objectId',
    userMap,
    firstUser,
    secondUser,
    setFirstResource,
    setSecondResource
  );

  useFetchMissingItems<TTEUser, string>({
    itemIds: selectedUserIds,
    itemMap: userMap,
    fetchItemsFn: (ids) => dispatch(fetchUsersByIds(ids)),
  });

  const removeAuthSettingsFromUser = (isFirstUser: boolean) => {
    if (isFirstUser && firstUser) {
      const { authSettings, ...rest } = firstUser;
      setFirstSelectedUser(rest);
    } else if (!isFirstUser && secondUser) {
      const { authSettings, ...rest } = secondUser;
      setSecondSelectedUser(rest);
    }
  };

  const onClose = (isFirstUser: boolean) =>
    confirmDiscardUserChanges(isFirstUser, () => {
      isFirstUser
        ? dispatch(setFirstSelectedUser(undefined))
        : dispatch(setSecondSelectedUser(undefined));
    });

  return (
    <>
      <TEDrawer
        expandWidth="70vw"
        firstDrawer={{
          title: `${firstUser?.firstName || ''} ${firstUser?.lastName || ''}`,
          content: (
            <UserDetail
              user={firstUser!}
              onChange={(key: string, value: any) =>
                dispatch(changeUser({ key, value, isFirstDrawer: true }))
              }
              removeAuthSettingsFromUser={() =>
                removeAuthSettingsFromUser(true)
              }
            />
          ),
          drawerOpen: !!firstUser,
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText('user_confirmation_delete')}
              handleDelete={() => dispatch(deleteUser(firstUser.id!))}
              handleDiscard={() =>
                dispatch(setFirstSelectedUser(userMap[firstUser.id!]))
              }
              handleSave={() =>
                dispatch(saveUser({ user: firstUser!, isFirstDrawer: true }))
              }
              isSaveDisable={firstUserSaveDisabled}
            />
          ),
          setDrawerOpen: (open) => !open && onClose(true),
        }}
        secondDrawer={{
          title: `${secondUser?.firstName || ''} ${secondUser?.lastName || ''}`,
          content: (
            <UserDetail
              user={secondUser!}
              onChange={(key: string, value: any) =>
                dispatch(changeUser({ key, value, isFirstDrawer: false }))
              }
              removeAuthSettingsFromUser={() =>
                removeAuthSettingsFromUser(false)
              }
            />
          ),
          drawerOpen: !!secondUser,
          extra: (
            <DrawerExtraButtons
              confirmDeleteMsg={getText('user_confirmation_delete')}
              handleDelete={() => dispatch(deleteUser(secondUser.id!))}
              handleDiscard={() =>
                dispatch(setSecondSelectedUser(userMap[secondUser.id!]))
              }
              handleSave={() =>
                dispatch(saveUser({ user: secondUser!, isFirstDrawer: false }))
              }
              isSaveDisable={secondUserSaveDisabled}
            />
          ),
          setDrawerOpen: (open) => !open && onClose(false),
        }}
        onExpand={() => {}}
      />
    </>
  );
};
